import axios from '../helpers/axios';
import axiosWithoutCredentials from 'axios';
import history from '../helpers/history';
import ImageCompressor from '@xkeshi/image-compressor';
import queryString from 'query-string';
import { notify } from 'reapop';
import { changeModalState } from '../modules/actions/modals';
import { get } from 'lodash';
import { fixedUserIds } from './fixedUserIds';
import { isLocalhost } from '../registerServiceWorker';
import { setCookie } from '../helpers/cookies';
import type { AppThunk, Notify } from '../typings/redux';
import { getTranslationKey } from '../modules/utils';
import type { EndpointKey } from '../modules/types/uploadEndpoint';
import { broadcastUploadEndpoint } from '../helpers/uploadEndpointBroadcast';
import { waitForIntercom } from '../helpers/waitForIntercom';
import moment from 'moment';

interface ModalStatePayload {
  [key: string]: boolean | string | number;
}

interface RegisterUserData {
  email: string;
  first_name: string;
  last_name: string;
  street: string;
  zipcode: string;
  city: string;
  type?: string;
  [key: string]: unknown;
}

interface LoginUserData {
  email: string;
  password: string;
}

interface EmailSubscriptionState {
  [key: string]: boolean | string | number;
}

interface UpdateUserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  street?: string;
  zipcode?: string;
  city?: string;
  company?: string;
  [key: string]: unknown;
}

export const logUserActivity =
  (data: unknown): AppThunk =>
  () => {
    try {
      axios.post('/api/useractivitylog/logaction', data);
    } catch (e) {
      console.log(e);
    }
  };

export const updateDefaultUserLanguages =
  (data: { noReload?: boolean; [key: string]: unknown }): AppThunk<Promise<void>> =>
  async () => {
    try {
      await axios.put('/api/me/updateDefaultUserLanguages', data);

      if (!data.noReload) window.location.reload();
    } catch (e) {
      console.log(e);
    }
  };

export const updateUploadEndpoint =
  (data: { uploadEndpoint: EndpointKey }): AppThunk<Promise<void>> =>
  async (dispatch, getState: any) => {
    const { _id: userId, uploadEndpoint: previous } = getState().auth ?? {};
    // Optimistic: flip the redux value first so any subsequent upload fork
    // (uploadImageSaga reads it via `yield select(getChosenUploadEndpoint)`)
    // sees the new endpoint immediately, without waiting for the BE round-trip.
    dispatch({ type: 'SET_UPLOAD_ENDPOINT', payload: data.uploadEndpoint });
    try {
      await axios.patch(`/api/users/${userId}`, data);
      broadcastUploadEndpoint(data.uploadEndpoint);
      dispatch(
        notify({
          title: getTranslationKey('success'),
          message: getTranslationKey('saved'),
          status: 'success',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 1500
        })
      );
    } catch (e) {
      dispatch({ type: 'SET_UPLOAD_ENDPOINT', payload: previous ?? null });
      console.log(e);
    }
  };

export const setUpgradeAccountOpenState =
  (payload: ModalStatePayload): AppThunk =>
  (dispatch) =>
    dispatch({
      type: 'SET_MODAL_STATE',
      payload
    });

export const setCollectionsModalOpenState =
  (payload: ModalStatePayload): AppThunk =>
  (dispatch) =>
    dispatch({
      type: 'SET_MODAL_STATE',
      payload
    });

export const activateMobileRestrictionModal = (): AppThunk => (dispatch) =>
  dispatch(changeModalState({ key: 'mobileRestrictionModal', state: true }));

export const activateNewUserModalState = (): AppThunk => (dispatch) =>
  dispatch(changeModalState({ key: 'newCustomerModal', state: true }));

export const setUpgradeSlideshowAccountOpenState =
  (payload: ModalStatePayload): AppThunk =>
  (dispatch) =>
    dispatch({
      type: 'SET_UPGRADE_SLIDESHOW_ACCOUNT_OPEN_STATE',
      payload
    });

export const redeemDiscountKey =
  (
    values: { discountKey: string },
    notify?: Notify,
    handleClose?: () => void
  ): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const result = await axios.put('/api/payment/discountKey/redeem', {
        discountKey: values.discountKey
      });

      if (result.data.redeemResultStatus === 200) {
        dispatch({ type: 'SET_USER_DIGISTORE', payload: result.data.digistore });
        notify &&
          notify({
            title: getTranslationKey('success'),
            message: getTranslationKey('pricingTables.discountKeyRedeemed'),
            status: 'success',
            position: 'top-center',
            dismissible: true,
            dismissAfter: 5000
          });
        handleClose && handleClose();
      } else if (result.data.redeemResultStatus === 404) {
        notify &&
          notify({
            title: getTranslationKey('error'),
            message: getTranslationKey('pricingTables.discountKeyNotFound'),
            status: 'error',
            position: 'top-center',
            dismissible: true,
            dismissAfter: 5000
          });
      }
    } catch (e) {
      notify &&
        notify({
          title: getTranslationKey('error'),
          message: getTranslationKey('generalError'),
          status: 'error',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 5000
        });
    }
  };

export const registerUser =
  (user: RegisterUserData, notify: Notify): AppThunk<Promise<boolean>> =>
  async () => {
    try {
      const signupResult = await axios.post('/api/signup', user);

      if (signupResult.data.temporaryLoginUrl) {
        window.location.href = signupResult.data.temporaryLoginUrl;

        return true;
      }
      if (user.type === 'VST') {
        return true;
      }
      notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('generalError'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 15000
      });

      return false;
    } catch (e: unknown) {
      const axiosError = e as { response?: { status?: number } };
      const message =
        axiosError?.response?.status === 422
          ? getTranslationKey('userAlreadyExists')
          : getTranslationKey('generalError');
      notify({
        title: getTranslationKey('error'),
        message,
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 15000
      });

      return false;
    }
  };

export const restartSubscription =
  (notify: Notify): AppThunk<Promise<void>> =>
  async () => {
    try {
      const res = await axios.post('/api/subscription/restart');

      if (res.data.result === 'success') {
        if (res.data.rebilling_start_url) {
          window.location.href = res.data.rebilling_start_url;
        } else {
          notify({
            title: getTranslationKey('success'),
            message: getTranslationKey('restartSubscriptionSuccessfull'),
            status: 'info',
            position: 'top-center',
            dismissible: true,
            dismissAfter: 15000
          });

          window.setTimeout(() => window.location.reload(), 5000);
        }
      } else {
        notify({
          title: getTranslationKey('error'),
          message: getTranslationKey('generalError'),
          status: 'error',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 15000
        });
      }
    } catch (e) {
      notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('generalError'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 15000
      });
    }
  };

export const cancelAbo =
  (notify: Notify): AppThunk<Promise<void>> =>
  async () => {
    try {
      if (window.confirm(getTranslationKey('rlyCancelAbo'))) {
        const res = await axios.post('/api/subscription/cancel-subscription');

        if (res.data.result === 'success') {
          notify({
            title: getTranslationKey('success'),
            message: getTranslationKey('cancelAboSuccess'),
            status: 'info',
            position: 'top-center',
            dismissible: true,
            dismissAfter: 0
          });

          window.setTimeout(() => window.location.reload(), 5000);
        } else {
          notify({
            title: getTranslationKey('error'),
            message: getTranslationKey('generalError'),
            status: 'error',
            position: 'top-center',
            dismissible: true,
            dismissAfter: 15000
          });
        }
      }
    } catch (e) {
      notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('generalError'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 15000
      });
    }
  };

export const dispatchAction =
  (type: string, payload?: unknown): AppThunk =>
  (dispatch) =>
    dispatch({ type, payload });

export const loginUser =
  (user: LoginUserData, notify: Notify): AppThunk<Promise<void>> =>
  async () => {
    try {
      const currentOrigin = `${window.location.protocol}//${window.location.host}`;
      const loginOrigin = encodeURIComponent(currentOrigin);
      const query = window.location.href.split('?')[1];
      const loginResult = await axios.post(`/api/signin?${query}`, {
        ...user,
        loginOrigin,
        isAjax: true
      });

      if (loginResult?.data?.redirectUrl) window.location.href = loginResult.data.redirectUrl;
      if (isLocalhost) window.location.reload();
    } catch (e) {
      notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('loginError'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 5000
      });
      window.location.reload();
    }
  };

export const logoutUser = (): AppThunk<Promise<void>> => async () => {
  window.localStorage.removeItem('auth');
  window.localStorage.removeItem('subscriptionConfig');
  setCookie('rescrictionModalClosed', '', 0);

  if (get(window, 'Intercom.booted')) {
    window.Intercom && window.Intercom('shutdown');
  }

  window.location.href = `${window.API_URL}/api/logout`;
};

export const deleteMe = (): AppThunk<Promise<boolean>> => async () => {
  try {
    if (window.confirm(getTranslationKey('rlyDeleteUserAccount'))) {
      await axios.delete('/api/me');

      return true;
    }

    return false;
  } catch (e) {
    console.log(e);

    return false;
  }
};

export const checkMaintenance = (): AppThunk<Promise<void>> => async (dispatch) => {
  try {
    const res = await axios.get('/api/maintenance');

    dispatch({
      type: 'SET_MAINTENANCE_MODE',
      payload: res.data.maintenance_mode
    });
  } catch (e) {
    console.log(e);
  }
};

export const getCurrentUser = (): AppThunk<Promise<void>> => async (dispatch) => {
  try {
    const query = queryString.parse(history.location.search);

    const res = await axios.get('/api/me', { params: query });

    if (res.data === false) {
      dispatch({ type: 'UNAUTH_USER' });
      window.localStorage.removeItem('auth');
      window.localStorage.removeItem('subscriptionConfig');
      window.CURRENT_USER = null;
    } else {
      const {
        _country,
        email,
        subscription,
        type,
        first_name,
        _id,
        intercomUserHash,
        languageSoftware,
        languageApp,
        created_at
      } = res.data;

      window.CURRENT_USER_ID = _id;
      window.CURRENT_USER = res.data;

      if (window.analytics && !window.localStorage.getItem('auth') && type === 'SB') {
        const userId = fixedUserIds.includes(_id) ? `fix_${_id}` : _id;
        window.analytics.identify(
          userId,
          { email, first_name, _country, subscription, languageSoftware, languageApp },
          { integrations: { Intercom: { user_hash: intercomUserHash } } }
        );

        if (created_at && moment(created_at).isAfter(moment().subtract(2, 'minutes'))) {
          waitForIntercom().then((isIntercomReady) => {
            if (isIntercomReady) {
              if (process.env.REACT_APP_REGION === 'eu') {
                if (languageSoftware === 0) window.Intercom?.('startSurvey', 1342641);
                else window.Intercom?.('startSurvey', 154684);
              }
            }
          });
        }
      }

      window.localStorage.setItem('auth', 'true');
      dispatch({ type: 'AUTH_USER' });
      dispatch({ type: 'SET_USER', payload: res.data });
    }
  } catch (e) {
    console.log(e);
  }
};

export const updateEmailSubscription =
  (state: EmailSubscriptionState, notify: Notify): AppThunk<Promise<void>> =>
  async () => {
    try {
      await axios.post('/api/hubspot/updateEmailSubscription/', { state });
      window.location.reload();
    } catch (e) {
      notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('generalError'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 15000
      });
      console.log(e);
    }
  };

export const changeCurrentUserPassword =
  (password: string, notify?: Notify, redirect: boolean = true): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      await axios.put('/api/me/change_password', { password });
      notify &&
        notify({
          title: getTranslationKey('success'),
          message: getTranslationKey('changedPassword'),
          status: 'success',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 5000
        });

      dispatch({ type: 'set_registration_process', payload: { key: 'setPassword', value: true } });
      redirect && history.push('/collections');
    } catch (e) {
      notify &&
        notify({
          title: getTranslationKey('error'),
          message: getTranslationKey('generalError'),
          status: 'error',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 15000
        });
    }
  };

export const forgotPassword =
  (email: string, notify: Notify): AppThunk<Promise<void>> =>
  async () => {
    try {
      await axios.post('/api/me/forgot_password', {
        email: email.toLowerCase(),
        type: window.USER_TYPE
      });
      notify({
        title: getTranslationKey('success'),
        message: getTranslationKey('forgotPasswordSuccess'),
        status: 'success',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 15000
      });
    } catch (e: unknown) {
      const axiosError = e as { response?: { status?: number } };
      const message =
        axiosError?.response?.status === 404
          ? getTranslationKey('emailNotExist')
          : getTranslationKey('generalError');
      notify({
        title: getTranslationKey('error'),
        message,
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 15000
      });
    }
  };

export const setCurrentUserValue =
  (data: { notSetInUI?: boolean; [key: string]: unknown }): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      await axios.put('/api/me/setValue', data);
      !data.notSetInUI && dispatch({ type: 'SET_USER_VALUE', payload: data });
    } catch (e) {
      console.log(e);
    }
  };

export const updateCurrentUser =
  (
    user: UpdateUserData,
    notify?: Notify,
    redirect: string | false = '/collections',
    changedEmail: boolean = false
  ): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      // SBK-9026: the account country is the legal address only. Changing it
      // must not touch the currency — that follows the billing address country
      // (see the billing-address saga in modules/sagas/subscription).
      await axios.put('/api/me/', user);

      dispatch({ type: 'SET_USER', payload: user });

      if (notify) {
        notify({
          title: getTranslationKey('success'),
          message: getTranslationKey('changedAccountData').replace('$', user.first_name),
          status: 'success',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 5000
        });

        if (changedEmail) {
          notify({
            title: getTranslationKey('actionRequired'),
            message: getTranslationKey('changedAccountEmail'),
            status: 'warning',
            position: 'top-center',
            dismissible: true,
            dismissAfter: 30000
          });
        }
      }

      if (redirect) history.push(redirect);
    } catch (e) {
      notify &&
        notify({
          title: getTranslationKey('error'),
          message: getTranslationKey('generalError'),
          status: 'error',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 15000
        });
    }
  };

export const uploadDefaultUserImage =
  ({
    type,
    userBrandId,
    file,
    notify
  }: {
    type: string;
    userBrandId: string;
    file: File;
    notify?: Notify;
  }): AppThunk<
    Promise<{ cryptImageName: string; originalImageName: string; url: string } | undefined>
  > =>
  async () => {
    // Ensure explicit return type path; return undefined on fall-through
    try {
      const res = await axios.get('/api/me/upload/defaultUserImage', {
        params: { objectName: file.name, type, userBrandId }
      });

      const imageUploadRes = res.data;

      let imageFile: File | Blob;
      if (!imageUploadRes.compress) imageFile = file;
      else {
        const imageCompressor = new ImageCompressor();
        imageFile = await imageCompressor.compress(file, {
          quality: 0.7,
          maxWidth: 1920,
          maxHeight: 1080
        });
      }
      await axiosWithoutCredentials.put(imageUploadRes.signedUrl, imageFile);

      const img = {
        cryptImageName: imageUploadRes.cryptImageName,
        originalImageName: imageUploadRes.originalImageName,
        url: imageUploadRes.url
      };

      return img;
    } catch (e) {
      notify &&
        notify({
          title: getTranslationKey('error'),
          message: getTranslationKey('generalError'),
          status: 'error',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 15000
        });

      return undefined;
    }
  };
